import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { bindAttendanceAgent } from "./attendanceMachineApi";
import { handleAttendanceApiError } from "./attendanceApi";
import { WebStorage, WebStorageNames } from "../../../utils";
import "./Attendance.css";

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  const n = parseInt(String(r), 10);
  return Number.isFinite(n) ? n : null;
};

const isCompanyAdmin = (rid) => rid === 1 || rid === 2;

/** Only allow loopback callbacks from the desktop agent. */
const isAllowedAgentRedirect = (uri) => {
  if (!uri || typeof uri !== "string") return false;
  try {
    const u = new URL(uri);
    if (u.protocol !== "http:") return false;
    if (u.hostname !== "127.0.0.1" && u.hostname !== "localhost") return false;
    if (!u.pathname.startsWith("/callback")) return false;
    return true;
  } catch {
    return false;
  }
};

const readQuery = (search) => {
  const q = new URLSearchParams(search || "");
  return {
    clientInstanceId: q.get("client_instance_id") || q.get("clientInstanceId") || "",
    agentName: q.get("agent_name") || q.get("agentName") || "",
    codeChallenge: q.get("code_challenge") || q.get("codeChallenge") || "",
    redirectUri: q.get("redirect_uri") || q.get("redirectUri") || "",
  };
};

/**
 * Desktop agent OAuth-style bind landing page.
 * Query: client_instance_id, agent_name, code_challenge, redirect_uri
 * On success redirects to redirect_uri?code=&installation_id=
 */
const AttendanceAgentBindPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const roleId = getRoleId();
  const params = useMemo(() => readQuery(location.search), [location.search]);

  const [status, setStatus] = useState("idle"); // idle | binding | done | error
  const [error, setError] = useState("");
  const [companyName, setCompanyName] = useState("");

  const missingParams = !params.clientInstanceId || !params.codeChallenge || !params.redirectUri;
  const redirectOk = isAllowedAgentRedirect(params.redirectUri);
  const allowed = isCompanyAdmin(roleId);

  const runBind = useCallback(async () => {
    if (!allowed || missingParams || !redirectOk) return;
    setStatus("binding");
    setError("");
    try {
      const result = await bindAttendanceAgent({
        clientInstanceId: params.clientInstanceId,
        agentName: params.agentName,
        codeChallenge: params.codeChallenge,
        redirectUri: params.redirectUri,
      });
      setCompanyName(result.companyName || "");
      setStatus("done");
      const target = new URL(params.redirectUri);
      target.searchParams.set("code", result.bindCode);
      target.searchParams.set("bind_code", result.bindCode);
      if (result.installationId) target.searchParams.set("installation_id", result.installationId);
      window.location.assign(target.toString());
    } catch (e) {
      setStatus("error");
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.Message ??
        e?.message ??
        t("app.attendance.agentBind.errorGeneric");
      setError(msg);
      handleAttendanceApiError(e, t("app.attendance.agentBind.errorGeneric"));
    }
  }, [allowed, missingParams, redirectOk, params, t]);

  useEffect(() => {
    if (allowed && !missingParams && redirectOk && status === "idle") {
      runBind();
    }
  }, [allowed, missingParams, redirectOk, status, runBind]);

  return (
    <div className="px-3 px-md-4 py-5 att-attendance-route">
      <div className="att-page" style={{ maxWidth: 560, margin: "0 auto" }}>
        <div className="att-config-panel p-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <div className="att-config-icon-badge att-config-icon-badge--primary" aria-hidden>
              <i className="bi bi-fingerprint" />
            </div>
            <div>
              <h1 className="h4 mb-1">{t("app.attendance.agentBind.title")}</h1>
              <p className="text-muted mb-0 small">{t("app.attendance.agentBind.subtitle")}</p>
            </div>
          </div>

          {missingParams ? (
            <div className="alert alert-warning mb-0">{t("app.attendance.agentBind.missingParams")}</div>
          ) : !redirectOk ? (
            <div className="alert alert-danger mb-0">{t("app.attendance.agentBind.invalidRedirect")}</div>
          ) : !allowed ? (
            <div className="alert alert-warning mb-0">{t("app.attendance.agentBind.adminOnly")}</div>
          ) : status === "binding" || status === "idle" ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
              {t("app.attendance.agentBind.linking")}
            </div>
          ) : status === "done" ? (
            <div className="alert alert-success mb-0">
              {t("app.attendance.agentBind.success", { company: companyName || "—" })}
            </div>
          ) : (
            <div>
              <div className="alert alert-danger">{error || t("app.attendance.agentBind.errorGeneric")}</div>
              <button type="button" className="att-btn att-btn--save-primary" onClick={() => { setStatus("idle"); }}>
                {t("app.attendance.agentBind.retry")}
              </button>
            </div>
          )}

          {params.agentName ? (
            <p className="small text-muted mt-3 mb-0">
              {t("app.attendance.agentBind.agentLabel")}: <strong>{params.agentName}</strong>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AttendanceAgentBindPage;
